import { get } from 'svelte/store';
import { home_data } from 'src/stores/account/user';
import { save_home } from './home_helpers';

const FUNNELS_KEY = 'funnels';

export interface FunnelStepData {
	firstReached?: number;
	lastReached?: number;
	count?: number;
}

export interface FunnelRecord {
	name: string;
	steps: Record<string, FunnelStepData>;
	lastStepId?: string;
	lastTimestamp?: number;
	completed?: boolean;
	completedAt?: number;
	abandonments?: Array<{ stepId: string; timestamp: number }>;
	counters?: Record<string, number>;
}

export type FunnelsData = FunnelRecord[];

function getAllFunnels(): FunnelsData {
	try {
		const homeData = get(home_data);
		const funnels = homeData?.[FUNNELS_KEY];
		return Array.isArray(funnels) ? funnels : [];
	} catch {
		return [];
	}
}

function getOrCreateFunnel(funnels: FunnelsData, name: string): FunnelRecord {
	let funnel = funnels.find((f) => f.name === name);
	if (!funnel) {
		funnel = { name, steps: {} };
		funnels.push(funnel);
	}
	return funnel;
}

export interface Funnel {
	readonly name: string;
	getRecord(): FunnelRecord | null;
	trackStep(stepId: string): Promise<void>;
	trackComplete(): Promise<void>;
	trackAbandon(stepId: string): Promise<void>;
	incrementCounter(counterId: string): Promise<void>;
}

class FunnelInstance implements Funnel {
	readonly name: string;

	constructor(name: string) {
		this.name = name;
	}

	getRecord(): FunnelRecord | null {
		return getAllFunnels().find((f) => f.name === this.name) || null;
	}

	async trackStep(stepId: string): Promise<void> {
		try {
			const funnels = [...getAllFunnels()];
			const now = Date.now();
			const funnel = getOrCreateFunnel(funnels, this.name);

			const step = funnel.steps[stepId] || {};
			step.firstReached = step.firstReached || now;
			step.lastReached = now;
			step.count = (step.count || 0) + 1;
			funnel.steps[stepId] = step;

			funnel.lastStepId = stepId;
			funnel.lastTimestamp = now;

			await save_home(FUNNELS_KEY, funnels);
		} catch (err) {
			console.error('Failed to track funnel step:', err);
		}
	}

	async trackComplete(): Promise<void> {
		try {
			const funnels = [...getAllFunnels()];
			const funnel = funnels.find((f) => f.name === this.name);
			if (funnel) {
				funnel.completed = true;
				funnel.completedAt = Date.now();
				await save_home(FUNNELS_KEY, funnels);
			}
		} catch (err) {
			console.error('Failed to track funnel completion:', err);
		}
	}

	async trackAbandon(stepId: string): Promise<void> {
		try {
			const funnels = [...getAllFunnels()];
			const funnel = getOrCreateFunnel(funnels, this.name);

			funnel.abandonments = funnel.abandonments || [];
			funnel.abandonments.push({ stepId, timestamp: Date.now() });

			await save_home(FUNNELS_KEY, funnels);
		} catch (err) {
			console.error('Failed to track funnel abandonment:', err);
		}
	}

	async incrementCounter(counterId: string): Promise<void> {
		try {
			const funnels = [...getAllFunnels()];
			const funnel = getOrCreateFunnel(funnels, this.name);

			funnel.counters = funnel.counters || {};
			funnel.counters[counterId] = (funnel.counters[counterId] || 0) + 1;

			await save_home(FUNNELS_KEY, funnels);
		} catch (err) {
			console.error('Failed to increment funnel counter:', err);
		}
	}
}

const funnelCache = new Map<string, Funnel>();

export function createFunnel(name: string): Funnel {
	let funnel = funnelCache.get(name);
	if (!funnel) {
		funnel = new FunnelInstance(name);
		funnelCache.set(name, funnel);
	}
	return funnel;
}

export function getAllFunnelRecords(): FunnelsData {
	return getAllFunnels();
}

export function getFunnelRecord(name: string): FunnelRecord | null {
	return getAllFunnels().find((f) => f.name === name) || null;
}
